import type { ReactNode } from 'react';
import { X } from '@phosphor-icons/react';
import { useVoiceRecorder, type RecorderErrorKind } from '../hooks/useVoiceRecorder';
import { useT } from '../i18n';
import { formatClock } from '../lib/voice-format';
import { MicCircle } from './MicCircle';
import { Button } from './ui';

interface RecordButtonProps {
  maxSeconds: number;
  disabled?: boolean;
  onRecorded: (blob: Blob) => void;
  onError: (kind: RecorderErrorKind) => void;
  /** Shown instead of the button on a phone that cannot record. */
  whenUnsupported?: ReactNode;
}

/** Tap to start, tap to stop; stops by itself at maxSeconds. For reading a text aloud. */
export function RecordButton({
  maxSeconds,
  disabled,
  onRecorded,
  onError,
  whenUnsupported,
}: RecordButtonProps) {
  const t = useT();
  const rec = useVoiceRecorder({
    maxDurationMs: maxSeconds * 1000,
    minDurationMs: 1000,
    onResult: (blob) => onRecorded(blob),
    onError,
  });

  if (rec.state === 'unsupported') {
    return <>{whenUnsupported ?? <p className="text-sm text-hint">{t.voiceSetup.unsupported}</p>}</>;
  }
  if (rec.state === 'recording') {
    // A slip while reading: throw the take away and start again, nothing is sent.
    return (
      <div className="flex flex-col items-center gap-2">
        <MicCircle recording stopIcon label={t.voiceSetup.stop} onClick={rec.stop} />
        <span className="text-sm tabular-nums text-hint">
          {t.voiceSetup.stop} · {formatClock(rec.durationMs)} / {formatClock(maxSeconds * 1000)}
        </span>
        <Button variant="ghost" size="sm" onClick={rec.cancel} leftIcon={<X size={14} />}>
          {t.voiceSetup.cancel}
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-2">
      <MicCircle
        label={t.voiceSetup.record}
        disabled={disabled || rec.state === 'pending'}
        onClick={() => void rec.start()}
      />
      <span className="text-sm text-hint">{t.voiceSetup.record}</span>
    </div>
  );
}
